import styled from "styled-components";
import { VehicleType } from "../vehicles/types";
import { formatDate, formatDateTime } from "../../utils/datetime";
import PdfPreview from "./components/PdfPreview";
import PdfPage from "./components/PdfPage";
import VehicleHeading from "./components/VehicleHeading";
import VehicleImages from "./components/VehicleImages";
import Header from "../../components/Header";
import VehicleInfo from "../vehicles/components/VehicleInfo";
import { isSeizureField } from "../vehicles/fields";
import env from "../../constants/env";
import { fieldToLabel } from "../vehicles/formConfig";

const S = {
	GroupWrapper: styled.div`
    margin-top: 60px;
    & > div:nth-child(2n) {
      background: #f5f5f5;
    }
  `,
	Header: styled.h1`
    margin: 16px 0 8px 0;
    padding-bottom: 4px;
    border-bottom: 1px solid black;
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
  `,
	DataLine: styled.div`
    display: grid;
    grid-template-columns: 1fr 2fr;
    padding: 4px 8px;
    min-width: 0;
    align-items: center;
    & > div {
      min-width: 0;
      font-size: 13px;
    }
  `,
	Note: styled.p`
    font-size: 18px;
  `,
};

const vehicleDetailsFields = [
	{ key: "make", fallback: "unknown" },
	{ key: "model", fallback: "unknown" },
	{ key: "vinNumber", fallback: "unknown" },
	{ key: "dateCreated", fallback: "unknown", formatter: formatDate },
	{ key: "dateOfTheft", fallback: "unknown", formatter: formatDate },
	{ key: "seizedLocationCity", fallback: "unknown" },
	{ key: "seizedLocationStreet", fallback: "unknown" },
	{ key: "datetimeSeized", fallback: "unknown", formatter: formatDate },
];

const VehicleDetails = ({ vehicle }: { vehicle: VehicleType }) => (
	<S.GroupWrapper>
		<S.Header>Vehicle Details</S.Header>
		{vehicleDetailsFields.map(({ key, fallback, formatter }) => (
			<S.DataLine key={key}>
				<div>{fieldToLabel(key)}</div>
				<div>
					{formatter
						? formatter(vehicle[key]) || fallback
						: vehicle[key] || fallback}
				</div>
			</S.DataLine>
		))}
	</S.GroupWrapper>
);

interface ReportProps {
	vehicle: VehicleType;
}

const SeizureReport = ({ vehicle }: ReportProps) => {
	const telematics = !vehicle.firstLocatedAt
		? ""
		: `The vehicle was first located on the ${vehicle.firstLocatedDate || "unknown date"} 
         at ${vehicle.firstLocatedAt || "unknown location"}.`;
	const fleetOwner = env.isKinto ? "Kinto" : "SIXT";
	const body = env.isFleet
		? `Regarding the vehicle identified by Vehicle Identification Number (VIN) ${
				vehicle.vinNumber
			}. Pursuant to a request from the legal owner of the vehicle, ${fleetOwner}, and through an assignment, DIGITPOL has successfully traced and secured the aforementioned vehicle.\n\nThe vehicle was initially located on ${
				formatDateTime(vehicle.firstLocatedDate) || "'unknown'"
			} at coordinates ${vehicle.firstLocatedAt || "'unknown'"}. ${
				vehicle.datetimeSeized
					? "It was subsequently seized and taken into custody on " +
						formatDateTime(vehicle.datetimeSeized)
					: ""
			}`
		: `
        With reference to the vehicle bearing the Vehicle Identity Number (VIN) of
        ${vehicle.vinNumber || "unknown vin"}. Digitpol activated, based on a request from the ${
					vehicle.clientName || "client"
				} 
        and an active SIS warrant, a tracking system in which enabled Digitpol to geolocate the 
        vehicle. ${telematics}
    `;
	const containsLongNote = (vehicle.note || "").length > 600;
	const vehicleInfo = (
		<VehicleInfo
			header="Seizure Info"
			vehicle={vehicle}
			fieldFilter={isSeizureField}
			compact
			filterEmptyFields
		/>
	);
	return (
		<PdfPreview>
			<PdfPage>
				<VehicleHeading fileName="Seizure Report" vehicle={vehicle} />
				<br />
				<p>{body}</p>
				<VehicleImages vehicle={vehicle} />
			</PdfPage>

			<PdfPage>
				<VehicleDetails vehicle={vehicle} />

				<br />
				{vehicle.note && <b>Intelligence</b>}
				<S.Note>{vehicle.note}</S.Note>

				<br />
				{!containsLongNote && vehicleInfo}
			</PdfPage>

			{containsLongNote && <PdfPage>{vehicleInfo}</PdfPage>}
		</PdfPreview>
	);
};

export default SeizureReport;
